import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

export function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function createNaturalMultiplicationTask({ type, pickData, explain }) {
  function generateTask() {
    return {
      type,
      data: pickData(),
      tags: ["liczby_naturalne", "mnozenie"],
    };
  }

  function renderTask({ data }) {
    return (
      <div style={{ textAlign: "left" }}>
        <p>
          Oblicz: <InlineMath math={`${data.a}\\cdot ${data.b}`} />.
        </p>
      </div>
    );
  }

  function renderInput(_task, userAnswer, onChange, disabled) {
    return (
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="wynik"
        placeholder="np. 24"
      />
    );
  }

  function checkAnswer({ data }, userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object") return false;
    const parsed = userAnswer.parsed;
    return Number.isFinite(parsed) && parsed === data.result;
  }

  function renderAnswer({ data }) {
    return <InlineMath math={`${data.result}`} />;
  }

  function renderUserAnswer(userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
      return <i>(brak odpowiedzi)</i>;
    }
    return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
  }

  function renderExplanation({ data }) {
    return explain(data);
  }

  return {
    generateTask,
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  };
}

export function zeroOneExplanation(data) {
  const special = data.a === 0 || data.b === 0 ? "zero" : "jeden";
  return (
    <div style={{ textAlign: "left" }}>
      {special === "zero" ? (
        <p>Gdy jeden czynnik jest równy 0, cały iloczyn jest równy 0.</p>
      ) : (
        <p>Mnożenie przez 1 nie zmienia liczby.</p>
      )}
      <p>
        <InlineMath math={`${data.a}\\cdot ${data.b}=${data.result}`} />
      </p>
    </div>
  );
}

export function tableExplanation(data) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>Mnożenie możemy rozumieć jako dodawanie tej samej liczby kilka razy.</p>
      <p>
        <InlineMath math={`${data.a}\\cdot ${data.b}`} /> oznacza{" "}
        <InlineMath math={`${data.b}`} /> grup po <InlineMath math={`${data.a}`} />.
      </p>
      <p>
        <InlineMath math={`${data.a}\\cdot ${data.b}=${data.result}`} />
      </p>
    </div>
  );
}

export function twoDigitByOneDigitExplanation(data) {
  const tens = Math.floor(data.a / 10) * 10;
  const ones = data.a % 10;
  return (
    <div style={{ textAlign: "left" }}>
      <p>Rozbijamy liczbę dwucyfrową na dziesiątki i jedności.</p>
      <p>
        <InlineMath math={`${data.a}=${tens}+${ones}`} />
      </p>
      <p>
        <InlineMath math={`${data.a}\\cdot ${data.b}=(${tens}+${ones})\\cdot ${data.b}`} />
      </p>
      <p>
        <InlineMath math={`${tens}\\cdot ${data.b}+${ones}\\cdot ${data.b}=${tens * data.b}+${ones * data.b}=${data.result}`} />
      </p>
    </div>
  );
}

export function fullTensExplanation(data) {
  const tensFactor = data.b / 10;
  return (
    <div style={{ textAlign: "left" }}>
      <p>Pełne dziesiątki traktujemy jako mnożenie przez liczbę jednocyfrową i przez 10.</p>
      <p>
        <InlineMath math={`${data.b}=${tensFactor}\\cdot 10`} />
      </p>
      <p>
        <InlineMath math={`${data.a}\\cdot ${data.b}=(${data.a}\\cdot ${tensFactor})\\cdot 10=${data.a * tensFactor}\\cdot 10=${data.result}`} />
      </p>
    </div>
  );
}

export function powerOfTenMultipleExplanation(data) {
  const compactFactor = data.b / data.place;
  const zeros = `${data.place}`.length - 1;
  return (
    <div style={{ textAlign: "left" }}>
      <p>Najpierw mnożymy przez liczbę bez zer, a potem dopisujemy zera z pełnej setki, tysiąca itd.</p>
      <p>
        <InlineMath math={`${data.b}=${compactFactor}\\cdot ${data.place}`} />
      </p>
      <p>
        <InlineMath math={`${data.a}\\cdot ${compactFactor}=${data.a * compactFactor}`} />
      </p>
      <p>
        Dopisujemy {zeros} {zeros === 1 ? "zero" : "zera"}:{" "}
        <InlineMath math={`${data.a * compactFactor}\\cdot ${data.place}=${data.result}`} />.
      </p>
    </div>
  );
}

export function twoDigitByTwoDigitExplanation(data) {
  const bTens = Math.floor(data.b / 10) * 10;
  const bOnes = data.b % 10;
  return (
    <div style={{ textAlign: "left" }}>
      <p>Drugą liczbę rozbijamy na dziesiątki i jedności, a potem dodajemy dwa iloczyny.</p>
      <p>
        <InlineMath math={`${data.b}=${bTens}+${bOnes}`} />
      </p>
      <p>
        <InlineMath math={`${data.a}\\cdot ${data.b}=${data.a}\\cdot ${bTens}+${data.a}\\cdot ${bOnes}`} />
      </p>
      <p>
        <InlineMath math={`${data.a * bTens}+${data.a * bOnes}=${data.result}`} />
      </p>
    </div>
  );
}

export function twoDigitByTwoDigitWithZerosExplanation(data) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>Najpierw oddzielamy zera, a mnożymy tylko liczby dwucyfrowe bez zer.</p>
      <p>
        <InlineMath math={`${data.a}=${data.baseA}\\cdot ${data.zeroFactorA}`} />
        ,{" "}
        <InlineMath math={`${data.b}=${data.baseB}\\cdot ${data.zeroFactorB}`} />.
      </p>
      <p>
        Mnożymy części bez zer:{" "}
        <InlineMath math={`${data.baseA}\\cdot ${data.baseB}=${data.baseProduct}`} />.
      </p>
      <p>
        Zera łączymy razem:{" "}
        <InlineMath math={`${data.zeroFactorA}\\cdot ${data.zeroFactorB}=${data.zeroFactor}`} />.
      </p>
      <p>
        <InlineMath math={`${data.baseProduct}\\cdot ${data.zeroFactor}=${data.result}`} />.
      </p>
    </div>
  );
}
